import React, { FC, useCallback } from 'react';
import { Checkbox, Grid, InputAdornment, TextField } from '@material-ui/core';
import { Control, Controller, useWatch } from 'react-hook-form';
import { PostDeliveryOrderBody } from 'src/interfaces/api/DeliveryOrder';
import { createUseStyles } from 'src/theming';
import { QuoteArticleHardware, QuoteArticleVirtual } from 'src/interfaces/api/Deal';
import Badge from 'src/components/Badge';

const useStyles = createUseStyles({
  root: {
    borderColor: '#919eab3d',
    borderBottomWidth: 1,
    borderBottomStyle: 'dashed',
    paddingTop: 10,
    paddingBottom: 5,
  },
});

interface Props {
  index: number;
  product: undefined | QuoteArticleHardware | QuoteArticleVirtual;
  control: Control<PostDeliveryOrderBody>;
  onRemove: () => void;
}

const Product: FC<Props> = ({ index, control, onRemove, product }) => {
  const classes = useStyles();
  const enabled = useWatch({ name: `articles.${index}.enabled` as const, control });
  const productReferenceId = useWatch({ name: `articles.${index}.productReferenceId` as const, control });

  const getQuantityDiff = useCallback(
    (actualQuantity: string) => {
      if (product?.quantity) {
        return parseInt(actualQuantity, 10) - product.quantity;
      } else {
        return 0;
      }
    },
    [product]
  );

  return (
    <Grid className={classes.root} container spacing={2}>
      <Grid item xs={2} sm={1}>
        <Controller
          name={`articles.${index}.enabled` as const}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
            <Checkbox checked={value} onChange={productReferenceId ? onChange : onRemove} {...field} />
          )}
        />
      </Grid>

      {!productReferenceId && (
        <Grid item xs={4} sm={2}>
          <Controller
            name={`articles.${index}.code` as const}
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label='Code'
                variant='outlined'
                size='small'
                fullWidth
                disabled={!enabled}
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
      )}

      <Grid item xs={productReferenceId ? 10 : 6} sm={productReferenceId ? 4 : 3}>
        <Controller
          name={`articles.${index}.name` as const}
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label='Nomenclature'
              variant='outlined'
              size='small'
              fullWidth
              disabled={!enabled}
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={9} sm={productReferenceId ? 5 : 4}>
        <Controller
          name={`articles.${index}.description` as const}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label='Description'
              variant='outlined'
              size='small'
              fullWidth
              inputProps={{ maxLength: 500 }}
              multiline
              disabled={!enabled}
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={3} sm={2}>
        <Controller
          name={`articles.${index}.quantity` as const}
          control={control}
          rules={{ required: { value: true, message: 'Requis' } }}
          render={({ field: { onChange, ...otherField }, fieldState: { error } }) => (
            <TextField
              {...otherField}
              label='Quantité'
              variant='outlined'
              size='small'
              fullWidth
              type='number'
              onChange={(e) => onChange(parseInt(e.target.value, 10))}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: 0 !== getQuantityDiff(otherField.value || '0') && (
                  <InputAdornment position='end'>
                    <Badge
                      text={`${getQuantityDiff(otherField.value || '0') > 0 ? '+' : ''}${getQuantityDiff(
                        otherField.value || '0'
                      )}`}
                      color={getQuantityDiff(otherField.value || '0') > 0 ? 'success' : 'error'}
                    />
                  </InputAdornment>
                ),
              }}
              disabled={!enabled}
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default Product;
